// benefitUtils.js
// Small pure helpers shared by the benefit cards + fulfillment modal.
// Kept framework-free so they're easy to unit test.

/**
 * A benefit (from /benefit/query) can have 0..n fulfillments, and each
 * fulfillment can have 0..n rules. Rules can carry a wallet amount, a
 * credit count, a copay %, a spend cap, and/or a per-transaction limit.
 * This walks every fulfillment/rule and produces one concise summary
 * to show on the card face.
 */
export function getBenefitAmount(benefit) {
  const fulfillments = Array.isArray(benefit?.fulfillments)
    ? benefit.fulfillments
    : [];

  let totalWallet = 0;
  let totalCredit = 0;
  let firstCopay = null;

  fulfillments.forEach((f) => {
    (f?.rules || []).forEach((rule) => {
      if (typeof rule?.wallet === "number") totalWallet += rule.wallet;
      if (typeof rule?.credit === "number") totalCredit += rule.credit;
      if (!firstCopay && rule?.copay && typeof rule.copay.value === "number") {
        firstCopay = rule.copay;
      }
    });
  });

  if (totalWallet > 0) {
    return {
      type: "wallet",
      label: `₹${totalWallet.toLocaleString("en-IN")}`,
      sub: "wallet",
    };
  }

  if (totalCredit > 0) {
    return {
      type: "credit",
      label: `${totalCredit}`,
      sub: totalCredit === 1 ? "credit" : "credits",
    };
  }

  if (firstCopay) {
    return {
      type: "copay",
      label: `${firstCopay.value}${firstCopay.percentage ? "%" : ""}`,
      sub: "copay",
    };
  }

  return null;
}

/** Human labels for the raw rule keys used inside the fulfillment modal. */
export function formatRule(rule) {
  if (!rule) return [];
  const chips = [];

  if (typeof rule.wallet === "number") {
    chips.push({ label: "Wallet", value: `₹${rule.wallet.toLocaleString("en-IN")}` });
  }
  if (typeof rule.credit === "number") {
    chips.push({ label: "Credit", value: `${rule.credit}` });
  }
  if (rule.copay && typeof rule.copay.value === "number") {
    chips.push({
      label: "Copay",
      value: `${rule.copay.value}${rule.copay.percentage ? "%" : ""}`,
    });
  }
  if (rule.spendCapacity && typeof rule.spendCapacity.value === "number") {
    chips.push({
      label: `Spend cap / ${rule.spendCapacity.period?.toLowerCase() || "period"}`,
      value: `₹${rule.spendCapacity.value.toLocaleString("en-IN")}`,
    });
  }
  if (typeof rule.perTransactionLimit === "number") {
    chips.push({
      label: "Per transaction limit",
      value: `₹${rule.perTransactionLimit.toLocaleString("en-IN")}`,
    });
  }
  if (rule.waitingPeriod && typeof rule.waitingPeriod.value === "number") {
    const period = rule.waitingPeriod.period?.toLowerCase() || "period";
    const unit = rule.waitingPeriod.value === 1 ? period : `${period}s`;
    chips.push({
      label: "Waiting Period",
      value: `${rule.waitingPeriod.value} ${unit}`,
    });
  }

  return chips;
}

/** Stable unique id for a benefit, since the API uses `id` not `planId`. */
export function getBenefitId(benefit, fallbackIndex) {
  return benefit?.id ?? benefit?.name ?? `benefit-${fallbackIndex}`;
}